import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useWishlist } from '@/context/WishlistContext';
import { Lock, Tag, Clock, Sparkles, Heart } from 'lucide-react';
import { getImageUrl } from '@/lib/api';

const ProductCard = ({ product }) => {
  const { isAuthenticated } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [imageLoaded, setImageLoaded] = useState(false);
  const inWishlist = isInWishlist(product.id);
  const totalStock = product.inventory?.reduce((acc, inv) => acc + inv.quantity, 0) || 0;
  const isOutOfStock = totalStock === 0 || product.is_out_of_stock;
  const lowStock = totalStock > 0 && totalStock < 50 && !product.is_out_of_stock;
  const isStockLot = product.is_stock_lot && product.is_discontinued;
  const isOnSale = product.is_on_sale && !isStockLot;
  const isSoldOut = product.is_sold_out || (isStockLot && totalStock === 0);
  const isComingSoon = product.is_coming_soon;

  const imageUrl = getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400';

  const formatExpectedDate = () => {
    if (!product.expected_date) return '';
    const date = new Date(product.expected_date);
    return date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  };

  const saleTypeLabels = {
    'black_friday': 'BLACK FRIDAY',
    'boxing_day': 'BOXING DAY',
    'australia_day': 'AUSTRALIA DAY',
    'christmas': 'CHRISTMAS SALE',
    'new_year': 'NEW YEAR SALE',
    'eofy': 'EOFY SALE',
    'end_of_season': 'END OF SEASON',
    'clearance': 'CLEARANCE',
    'flash_sale': 'FLASH SALE',
  };

  const getSaleBadgeText = () => {
    if (product.sale_type === 'custom' && product.sale_type_custom) {
      return product.sale_type_custom.toUpperCase();
    }
    return saleTypeLabels[product.sale_type] || 'SALE';
  };

  const originalTotal = isStockLot ? totalStock * product.price : 0;
  const stockLotPrice = product.stock_lot_price || 0;
  const stockLotDiscount = originalTotal > 0 ? Math.round((1 - stockLotPrice / originalTotal) * 100) : 0;
  const saleDiscount = isOnSale && product.price > 0 
    ? Math.round((1 - (product.sale_price || 0) / product.price) * 100) 
    : 0;

  return (
    <Link
      to={`/products/${product.id}`}
      className="group block"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-4 relative">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse" />
        )}
        <img
          src={imageUrl}
          alt={product.name}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400';
            setImageLoaded(true);
          }}
        />
        
        {/* Wishlist Heart */}
        {isAuthenticated && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleWishlist(product.id, product.name);
            }}
            className={`absolute top-3 right-3 z-20 p-2 rounded-full transition-all duration-200 ${
              inWishlist 
                ? 'bg-red-500 text-white' 
                : 'bg-white/80 text-gray-400 hover:text-red-500 hover:bg-white'
            }`}
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart className={`w-4 h-4 ${inWishlist ? 'fill-current' : ''}`} />
          </button>
        )}
        
        {/* Coming Soon */}
        {isComingSoon && (
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 to-transparent flex flex-col justify-end p-4 z-10">
            <div className="flex flex-wrap gap-1 mb-1">
              {product.is_new && (
                <Badge className="bg-green-500 text-white text-[10px] px-2 py-1 font-bold">
                  <Sparkles className="w-3 h-3 mr-1" />
                  NEW
                </Badge>
              )}
              <Badge className="bg-blue-600 text-white text-[10px] px-2 py-1 font-bold">
                <Clock className="w-3 h-3 mr-1" />
                COMING SOON
              </Badge>
            </div>
            {product.expected_date && (
              <span className="text-white text-xs font-medium">
                Due in store: {formatExpectedDate()}
              </span>
            )}
          </div>
        )}
        
        {/* Sold Out */}
        {isSoldOut && !isComingSoon && (
          <div className="absolute top-3 left-3 z-10">
            <Badge className="bg-[#0F172A] text-white text-[10px] px-2 py-1 font-bold">
              SOLD OUT
            </Badge>
          </div>
        )}
        
        {/* Backorder */}
        {product.is_out_of_stock && !isSoldOut && !isComingSoon && (
          <div className="absolute top-3 left-3 z-10">
            <Badge className="bg-orange-500 text-white text-[10px] px-2 py-1 font-bold flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              BACKORDER
            </Badge>
          </div>
        )}
        
        {/* Stock Lot */}
        {isStockLot && !isSoldOut && !isComingSoon && (
          <div className="absolute top-3 left-3 flex flex-col gap-1 z-10">
            <Badge className="bg-orange-500 text-white text-[10px] px-2 py-0.5">
              <Tag className="w-3 h-3 mr-1" />
              STOCK LOT
            </Badge>
            {stockLotDiscount > 0 && (
              <Badge className="bg-green-500 text-white text-[10px] px-2 py-0.5">
                {stockLotDiscount}% OFF
              </Badge>
            )}
          </div>
        )}

        {/* Sale */}
        {isOnSale && !isSoldOut && !isComingSoon && (
          <div className="absolute top-3 left-3 flex flex-col gap-1 z-10">
            <Badge className="bg-red-500 text-white text-[10px] px-2 py-0.5 font-bold">
              {getSaleBadgeText()}
            </Badge>
            {saleDiscount > 0 && (
              <Badge className="bg-red-600 text-white text-[10px] px-2 py-0.5">
                {saleDiscount}% OFF
              </Badge>
            )}
          </div>
        )}
        
        {/* NEW */}
        {product.is_new && !isComingSoon && !isOnSale && !isStockLot && !isSoldOut && !product.is_out_of_stock && (
          <div className="absolute top-3 left-3 z-10">
            <Badge className="bg-green-500 text-white text-[10px] px-2 py-1 font-bold">
              <Sparkles className="w-3 h-3 mr-1" />
              NEW
            </Badge>
          </div>
        )}
        
        {/* Tag Badges */}
        {!isComingSoon && !isSoldOut && (
          <div className="absolute bottom-3 left-3 flex flex-wrap gap-1 z-10 max-w-[70%]">
            {product.is_best_seller && (
              <Badge className="bg-yellow-400 text-black text-[9px] px-1.5 py-0.5 font-bold">
                BEST SELLER
              </Badge>
            )}
            {product.is_top_seller && (
              <Badge className="bg-purple-600 text-white text-[9px] px-1.5 py-0.5 font-bold">
                TOP SELLER
              </Badge>
            )}
            {product.is_pre_order && (
              <Badge className="bg-blue-500 text-white text-[9px] px-1.5 py-0.5 font-bold">
                PRE-ORDER
              </Badge>
            )}
            {product.is_limited_edition && (
              <Badge className="bg-pink-600 text-white text-[9px] px-1.5 py-0.5 font-bold">
                LIMITED EDITION
              </Badge>
            )}
            {product.is_popular && (
              <Badge className="bg-indigo-500 text-white text-[9px] px-1.5 py-0.5 font-bold">
                POPULAR
              </Badge>
            )}
          </div>
        )}
        
        {/* Discontinued */}
        {product.is_discontinued && !isStockLot && !isSoldOut && (
          <Badge className="absolute top-3 left-3 bg-gray-500 text-white text-[10px] z-10">
            Discontinued
          </Badge>
        )}
      </div>

      {/* Product Info */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-[#0F172A] group-hover:text-[#002FA7] transition-colors line-clamp-2">
            {product.name}
          </h3>
          {isComingSoon && (
            <Badge variant="outline" className="text-xs border-blue-400 text-blue-600 shrink-0">
              Pre-Order
            </Badge>
          )}
          {isOutOfStock && !isStockLot && !isComingSoon && (
            <Badge variant="outline" className="text-xs border-red-400 text-red-600 shrink-0">
              Out of Stock
            </Badge>
          )}
          {lowStock && !isStockLot && !isOutOfStock && !isComingSoon && (
            <Badge variant="outline" className="text-xs border-orange-400 text-orange-600 shrink-0">
              Low Stock
            </Badge>
          )}
        </div>

        <p className="text-xs text-[#64748B] font-mono">{product.sku}</p>

        {/* Price */}
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            isStockLot ? (
              <>
                <span className="text-base font-bold text-green-600">${stockLotPrice.toFixed(2)}</span>
                <span className="text-xs text-[#94A3B8] line-through">${originalTotal.toFixed(2)}</span>
                <span className="text-xs text-green-600 font-medium">lot price</span>
              </>
            ) : isOnSale ? (
              <>
                <span className="text-base font-bold text-red-600">${product.sale_price?.toFixed(2)}</span>
                <span className="text-xs text-[#94A3B8] line-through">${product.price?.toFixed(2)}</span>
              </>
            ) : (
              <span className="text-base font-bold text-[#0F172A]">${product.price?.toFixed(2)}</span>
            )
          ) : (
            <div className="flex items-center gap-1 text-[#94A3B8]">
              <Lock className="w-3 h-3" />
              <span className="text-xs">Login for price</span>
            </div>
          )}
        </div>

        {/* Stock & Sizes */}
        {!isStockLot && (
          <div className="flex items-center justify-between">
            <div className="flex gap-1 flex-wrap">
              {(() => {
                const sizingType = product.sizing_type || 'unisex';
                const allowedBySizingType = {
                  unisex:   ['S', 'M', 'L', 'XL', 'XXL'],
                  ladyfit:  ['S', 'M', 'L', 'XL'],
                  onesize:  ['OS'],
                  kids:     ['1-2', '3-4', '5-6', '7-8', '9-10', '11-12'],
                  combined: ['S/M', 'L/XL'],
                }[sizingType] || ['S', 'M', 'L', 'XL', 'XXL'];
                return (product.inventory || [])
                  .filter((inv) => allowedBySizingType.includes(inv.size))
                  .slice(0, 6)
                  .map((inv) => (
                    <span
                      key={inv.size}
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        inv.quantity > 0
                          ? 'bg-[#F1F5F9] text-[#475569]'
                          : 'bg-red-50 text-red-400 line-through'
                      }`}
                    >
                      {inv.size}
                    </span>
                  ));
              })()}
            </div>
            <span className={`text-xs ${isComingSoon ? 'text-blue-500 font-medium' : isOutOfStock ? 'text-red-500 font-medium' : 'text-[#64748B]'}`}>
              {isComingSoon ? 'Pre-Order' : isOutOfStock ? 'Out of stock' : `${totalStock} in stock`}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
